import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Icon } from '@iconify/react';
import MasterLayout from '../../masterLayout/MasterLayout';
import Breadcrumb from '../../components/Breadcrumb';
import PageHeader from '../../components/common/PageHeader';
import { selectUser } from '../../services/reducers/user';
import { API_URL } from '../../config/api';

const MyPurchasesPage = () => {
  const user = useSelector(selectUser);
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPurchase, setSelectedPurchase] = useState(null);
  const [showModal, setShowModal] = useState(false);
  
  // Filtros
  const [filters, setFilters] = useState({
    status: 'all',
    paymentMethod: 'all',
    dateRange: 'all'
  });

  // Carregar compras do usuário
  useEffect(() => {
    loadPurchases();
  }, [filters]);

  const loadPurchases = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('auth_token');
      const queryParams = new URLSearchParams({
        role: 'customer',
        ...(filters.status !== 'all' && { status: filters.status }),
        ...(filters.paymentMethod !== 'all' && { payment_method: filters.paymentMethod }),
        ...(filters.dateRange !== 'all' && getDateRangeParams(filters.dateRange))
      });

      const response = await fetch(`${API_URL}/api/sales?${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Erro ao carregar compras');
      }

      const data = await response.json();
      setPurchases(data.data || []);
      
    } catch (err) {
      setError(err.message);
      console.error('Erro ao carregar compras:', err);
    } finally {
      setLoading(false);
    }
  };

  const getDateRangeParams = (range) => {
    const now = new Date();
    const startDate = new Date();

    switch (range) {
      case 'last7days':
        startDate.setDate(now.getDate() - 7);
        break;
      case 'last30days':
        startDate.setDate(now.getDate() - 30);
        break;
      case 'last3months':
        startDate.setMonth(now.getMonth() - 3);
        break;
      default:
        return {};
    }

    return {
      start_date: startDate.toISOString().split('T')[0],
      end_date: now.toISOString().split('T')[0]
    };
  };

  const openPurchaseDetails = async (purchaseId) => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/api/sales/${purchaseId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Erro ao carregar detalhes da compra');
      }

      const data = await response.json();
      setSelectedPurchase(data.data);
      setShowModal(true);
    } catch (err) {
      setError(err.message);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'pending_payment': { color: 'warning', icon: 'solar:clock-circle-bold', text: 'Aguardando Pagamento' },
      'paid': { color: 'info', icon: 'solar:check-circle-bold', text: 'Pago' },
      'processing': { color: 'primary', icon: 'solar:settings-bold', text: 'Processando' },
      'shipped': { color: 'success', icon: 'solar:delivery-bold', text: 'Enviado' },
      'delivered': { color: 'success', icon: 'solar:home-2-bold', text: 'Entregue' },
      'cancelled': { color: 'danger', icon: 'solar:close-circle-bold', text: 'Cancelado' },
      'refunded': { color: 'secondary', icon: 'solar:restart-bold', text: 'Reembolsado' }
    };

    const config = statusConfig[status] || statusConfig['pending_payment'];
    
    return (
      <span className={`badge bg-${config.color} text-white d-flex align-items-center gap-1`}>
        <Icon icon={config.icon} className="text-xs" />
        {config.text}
      </span>
    );
  };

  const getPaymentMethodIcon = (method) => {
    const methods = {
      'credit_card': 'solar:card-bold',
      'debit_card': 'solar:card-2-bold',
      'pix': 'solar:qr-code-bold',
      'boleto': 'solar:document-text-bold',
      'after_pay': 'solar:dollar-circle-bold'
    };
    return methods[method] || 'solar:card-bold';
  };

  const getPaymentMethodText = (method) => {
    const methods = {
      'credit_card': 'Cartão de Crédito',
      'debit_card': 'Cartão de Débito',
      'pix': 'PIX',
      'boleto': 'Boleto',
      'after_pay': 'After Pay'
    };
    return methods[method] || method;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(parseFloat(price));
  };

  return (
    <MasterLayout>
      <Breadcrumb 
        title="Minhas Compras" 
        items={[
          { label: 'Início', path: '/Dashboard' },
          { label: 'Minhas Compras', path: '/my-purchases' }
        ]}
      />

      {/* Header Section */}
      <PageHeader
        icon="mdi:shopping"
        title="Minhas Compras"
        subtitle="Acompanhe todas as suas compras e histórico de transações"
      />

      <div className="dashboard-main-body">
        {/* Filtros */}
        <div className="card radius-12 mb-24">
          <div className="card-body p-20">
            <div className="row g-3">
              <div className="col-md-3">
                <label className="form-label text-sm fw-medium">Status</label>
                <select
                  className="form-select"
                  value={filters.status}
                  onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                >
                  <option value="all">Todos os Status</option>
                  <option value="pending_payment">Aguardando Pagamento</option>
                  <option value="paid">Pago</option>
                  <option value="processing">Processando</option>
                  <option value="shipped">Enviado</option>
                  <option value="delivered">Entregue</option>
                  <option value="cancelled">Cancelado</option>
                </select>
              </div>
              
              <div className="col-md-3">
                <label className="form-label text-sm fw-medium">Método de Pagamento</label>
                <select
                  className="form-select"
                  value={filters.paymentMethod}
                  onChange={(e) => setFilters(prev => ({ ...prev, paymentMethod: e.target.value }))}
                >
                  <option value="all">Todos os Métodos</option>
                  <option value="credit_card">Cartão de Crédito</option>
                  <option value="debit_card">Cartão de Débito</option>
                  <option value="pix">PIX</option>
                  <option value="boleto">Boleto</option>
                  <option value="after_pay">After Pay</option>
                </select>
              </div>
              
              <div className="col-md-3">
                <label className="form-label text-sm fw-medium">Período</label>
                <select
                  className="form-select"
                  value={filters.dateRange}
                  onChange={(e) => setFilters(prev => ({ ...prev, dateRange: e.target.value }))}
                >
                  <option value="all">Todo o período</option>
                  <option value="last7days">Últimos 7 dias</option>
                  <option value="last30days">Últimos 30 dias</option>
                  <option value="last3months">Últimos 3 meses</option>
                </select>
              </div>
              
              <div className="col-md-3 d-flex align-items-end">
                <button
                  onClick={loadPurchases}
                  className="btn btn-primary-600 text-white fw-medium w-100"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" />
                      Carregando...
                    </>
                  ) : (
                    <>
                      <Icon icon="solar:refresh-bold" className="me-2" />
                      Atualizar
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Lista de Compras */}
        <div className="card radius-12">
          <div className="card-body p-0">
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary mb-3" />
                <p className="text-muted">Carregando suas compras...</p>
              </div>
            ) : error ? (
              <div className="text-center py-5">
                <Icon icon="solar:close-circle-bold" className="text-danger mb-3" style={{ fontSize: '48px' }} />
                <h5 className="text-danger mb-2">Erro ao carregar compras</h5>
                <p className="text-muted mb-3">{error}</p>
                <button 
                  onClick={loadPurchases}
                  className="btn btn-primary-600 text-white"
                >
                  Tentar Novamente
                </button>
              </div>
            ) : purchases.length === 0 ? (
              <div className="text-center py-5">
                <Icon icon="solar:bag-smile-bold" className="text-secondary-light mb-3" style={{ fontSize: '64px' }} />
                <h5 className="text-secondary-light mb-2">Nenhuma compra encontrada</h5>
                <p className="text-muted mb-3">Você ainda não fez nenhuma compra ou não há compras nos filtros selecionados.</p>
                <a href="#/store/store-products" className="btn btn-primary-600 text-white">
                  <Icon icon="solar:bag-heart-bold" className="me-2" />
                  Explorar Produtos
                </a>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-striped">
                  <thead className="table-light">
                    <tr>
                      <th className="h6 text-gray-300">Pedido</th>
                      <th className="h6 text-gray-300">Produto</th>
                      <th className="h6 text-gray-300">Data</th>
                      <th className="h6 text-gray-300">Valor</th>
                      <th className="h6 text-gray-300">Pagamento</th>
                      <th className="h6 text-gray-300">Status</th>
                      <th className="h6 text-gray-300">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {purchases.map((purchase) => (
                      <tr key={purchase.id}>
                        <td>
                          <div className="d-flex flex-column">
                            <span className="fw-medium text-dark">#{purchase.id}</span>
                            {purchase.tracking_code && (
                              <small className="text-muted">
                                <Icon icon="solar:box-bold" className="me-1" />
                                {purchase.tracking_code}
                              </small>
                            )}
                          </div>
                        </td>
                        
                        <td>
                          <div className="d-flex align-items-center gap-3">
                            <div className="w-40-px h-40-px bg-primary-50 text-primary-600 radius-8 d-flex align-items-center justify-content-center flex-shrink-0">
                              <Icon icon="solar:box-bold" />
                            </div>
                            <div>
                              <h6 className="text-sm fw-medium mb-0 line-height-1">{purchase.product_name}</h6>
                              <span className="text-xs text-secondary-light">{purchase.producer_name}</span>
                            </div>
                          </div>
                        </td>
                        
                        <td>
                          <span className="text-sm text-secondary-light">
                            {formatDate(purchase.created_at)}
                          </span>
                        </td>
                        
                        <td>
                          <span className="text-sm fw-semibold text-dark">
                            {formatPrice(purchase.total_amount)}
                          </span>
                        </td>
                        
                        <td>
                          <div className="d-flex align-items-center gap-2">
                            <Icon 
                              icon={getPaymentMethodIcon(purchase.payment_method)} 
                              className="text-secondary-light"
                            />
                            <span className="text-xs">
                              {getPaymentMethodText(purchase.payment_method)}
                            </span>
                          </div>
                        </td>
                        
                        <td>
                          {getStatusBadge(purchase.sale_status)}
                        </td>
                        
                        <td>
                          <button
                            onClick={() => openPurchaseDetails(purchase.id)}
                            className="btn btn-outline-primary-600 text-primary-600 btn-sm fw-medium"
                          >
                            <Icon icon="solar:eye-bold" className="me-1" />
                            Detalhes
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Resumo Estatístico */}
        {purchases.length > 0 && (
          <div className="row mt-24">
            <div className="col-md-3">
              <div className="card radius-12 h-100">
                <div className="card-body text-center">
                  <Icon icon="solar:bag-bold" className="text-primary-600 mb-2" style={{ fontSize: '32px' }} />
                  <h4 className="fw-bold text-dark mb-1">{purchases.length}</h4>
                  <p className="text-sm text-secondary-light mb-0">Total de Compras</p>
                </div>
              </div>
            </div>
            
            <div className="col-md-3">
              <div className="card radius-12 h-100">
                <div className="card-body text-center">
                  <Icon icon="solar:dollar-circle-bold" className="text-success-600 mb-2" style={{ fontSize: '32px' }} />
                  <h4 className="fw-bold text-dark mb-1">
                    {formatPrice(
                      purchases.reduce((sum, p) => sum + parseFloat(p.total_amount), 0)
                    )}
                  </h4>
                  <p className="text-sm text-secondary-light mb-0">Valor Total</p>
                </div>
              </div>
            </div>
            
            <div className="col-md-3">
              <div className="card radius-12 h-100">
                <div className="card-body text-center">
                  <Icon icon="solar:delivery-bold" className="text-info-600 mb-2" style={{ fontSize: '32px' }} />
                  <h4 className="fw-bold text-dark mb-1">
                    {purchases.filter(p => p.sale_status === 'shipped' || p.sale_status === 'delivered').length}
                  </h4>
                  <p className="text-sm text-secondary-light mb-0">Enviados</p>
                </div>
              </div>
            </div>
            
            <div className="col-md-3">
              <div className="card radius-12 h-100">
                <div className="card-body text-center">
                  <Icon icon="solar:home-2-bold" className="text-warning-600 mb-2" style={{ fontSize: '32px' }} />
                  <h4 className="fw-bold text-dark mb-1">
                    {purchases.filter(p => p.sale_status === 'delivered').length}
                  </h4>
                  <p className="text-sm text-secondary-light mb-0">Entregues</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modal de Detalhes da Compra */}
      {showModal && selectedPurchase && (
        <div 
          className="modal fade show d-block" 
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
          onClick={() => setShowModal(false)}
        >
          <div 
            className="modal-dialog modal-lg modal-dialog-centered"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content">
              <div className="modal-header border-bottom">
                <h5 className="modal-title fw-bold">
                  <Icon icon="solar:document-text-bold" className="me-2 text-primary-600" />
                  Detalhes da Compra #{selectedPurchase.id}
                </h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => setShowModal(false)}
                />
              </div>
              
              <div className="modal-body p-24">
                {/* Produto */}
                <div className="d-flex align-items-center gap-3 mb-4 p-3 bg-neutral-50 radius-8">
                  <div className="w-60-px h-60-px bg-primary-50 text-primary-600 radius-8 d-flex align-items-center justify-content-center flex-shrink-0">
                    <Icon icon="solar:box-bold" style={{ fontSize: '24px' }} />
                  </div>
                  <div className="flex-grow-1">
                    <h6 className="fw-bold mb-1">{selectedPurchase.product_name}</h6>
                    <p className="text-sm text-secondary-light mb-0">{selectedPurchase.product_description}</p>
                    <span className="text-xs text-muted">Vendido por: {selectedPurchase.producer_name}</span>
                  </div>
                  <div className="text-end">
                    <h5 className="fw-bold text-primary-600 mb-0">
                      {formatPrice(selectedPurchase.total_amount)}
                    </h5>
                  </div>
                </div>

                {/* Informações da Compra */}
                <div className="row mb-4">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="text-sm fw-medium text-secondary-light">Data da Compra</label>
                      <p className="mb-0">{formatDate(selectedPurchase.created_at)}</p>
                    </div>
                    
                    <div className="mb-3">
                      <label className="text-sm fw-medium text-secondary-light">Status do Pedido</label>
                      <div className="mt-1">{getStatusBadge(selectedPurchase.sale_status)}</div>
                    </div>
                  </div>
                  
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="text-sm fw-medium text-secondary-light">Método de Pagamento</label>
                      <div className="d-flex align-items-center gap-2 mt-1">
                        <Icon icon={getPaymentMethodIcon(selectedPurchase.payment_method)} />
                        <span>{getPaymentMethodText(selectedPurchase.payment_method)}</span>
                      </div>
                    </div>
                    
                    <div className="mb-3">
                      <label className="text-sm fw-medium text-secondary-light">Status do Pagamento</label>
                      <div className="mt-1">
                        {getStatusBadge(selectedPurchase.payment_status)}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Rastreamento */}
                {selectedPurchase.tracking_code && (
                  <div className="alert alert-info mb-4">
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <Icon icon="solar:delivery-bold" />
                      <strong>Informações de Entrega</strong>
                    </div>
                    <p className="mb-1">
                      <strong>Código de Rastreamento:</strong> {selectedPurchase.tracking_code}
                    </p>
                    {selectedPurchase.shipping_company && (
                      <p className="mb-1">
                        <strong>Transportadora:</strong> {selectedPurchase.shipping_company}
                      </p>
                    )}
                    {selectedPurchase.shipped_at && (
                      <p className="mb-0">
                        <strong>Data de Envio:</strong> {formatDate(selectedPurchase.shipped_at)}
                      </p>
                    )}
                  </div>
                )}

                {/* Endereço de Entrega */}
                {selectedPurchase.shipping_address && (
                  <div className="mb-4">
                    <label className="text-sm fw-medium text-secondary-light">Endereço de Entrega</label>
                    <div className="bg-neutral-50 p-3 radius-8 mt-1">
                      {(() => {
                        try {
                          const address = typeof selectedPurchase.shipping_address === 'string' 
                            ? JSON.parse(selectedPurchase.shipping_address)
                            : selectedPurchase.shipping_address;
                          
                          return (
                            <div>
                              <p className="mb-1">{address.recipient || address.street}</p>
                              <p className="mb-1">{address.street} {address.number}</p>
                              {address.complement && <p className="mb-1">{address.complement}</p>}
                              <p className="mb-0">{address.neighborhood}, {address.city} - {address.state}</p>
                              <p className="mb-0">CEP: {address.zipCode || address.zip}</p>
                            </div>
                          );
                        } catch {
                          return <p className="mb-0 text-muted">Endereço não disponível</p>;
                        }
                      })()}
                    </div>
                  </div>
                )}

                {/* Timeline de Status */}
                <div className="mb-4">
                  <label className="text-sm fw-medium text-secondary-light mb-3 d-block">Timeline do Pedido</label>
                  <div className="timeline">
                    <div className="timeline-item">
                      <div className="timeline-marker bg-success"></div>
                      <div className="timeline-content">
                        <h6 className="mb-1">Pedido Realizado</h6>
                        <p className="text-sm text-muted mb-0">{formatDate(selectedPurchase.created_at)}</p>
                      </div>
                    </div>
                    
                    {selectedPurchase.payment_date && (
                      <div className="timeline-item">
                        <div className="timeline-marker bg-info"></div>
                        <div className="timeline-content">
                          <h6 className="mb-1">Pagamento Confirmado</h6>
                          <p className="text-sm text-muted mb-0">{formatDate(selectedPurchase.payment_date)}</p>
                        </div>
                      </div>
                    )}
                    
                    {selectedPurchase.shipped_at && (
                      <div className="timeline-item">
                        <div className="timeline-marker bg-warning"></div>
                        <div className="timeline-content">
                          <h6 className="mb-1">Pedido Enviado</h6>
                          <p className="text-sm text-muted mb-0">{formatDate(selectedPurchase.shipped_at)}</p>
                        </div>
                      </div>
                    )}
                    
                    {selectedPurchase.delivered_at && (
                      <div className="timeline-item">
                        <div className="timeline-marker bg-success"></div>
                        <div className="timeline-content">
                          <h6 className="mb-1">Pedido Entregue</h6>
                          <p className="text-sm text-muted mb-0">{formatDate(selectedPurchase.delivered_at)}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="modal-footer border-top">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={() => setShowModal(false)}
                >
                  Fechar
                </button>
                
                {selectedPurchase.tracking_code && (
                  <button 
                    type="button" 
                    className="btn btn-primary-600 text-white"
                    onClick={() => {
                      // Em produção, integrar com APIs de rastreamento
                      window.open(`https://www.correios.com.br/`, '_blank');
                    }}
                  >
                    <Icon icon="solar:delivery-bold" className="me-2" />
                    Rastrear Pedido
                  </button>
                )}
                
                <button 
                  type="button" 
                  className="btn btn-outline-primary-600 text-primary-600"
                  onClick={() => window.print()}
                >
                  <Icon icon="solar:printer-bold" className="me-2" />
                  Imprimir
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CSS para Timeline */}
      <style jsx>{`
        .timeline {
          position: relative;
          padding-left: 30px;
        }
        
        .timeline:before {
          content: '';
          position: absolute;
          left: 10px;
          top: 0;
          bottom: 0;
          width: 2px;
          background: #e9ecef;
        }
        
        .timeline-item {
          position: relative;
          margin-bottom: 20px;
        }
        
        .timeline-marker {
          position: absolute;
          left: -25px;
          top: 0;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          border: 3px solid white;
          box-shadow: 0 0 0 2px #e9ecef;
        }
        
        .timeline-content {
          padding-left: 10px;
        }
      `}</style>
    </MasterLayout>
  );
};

export default MyPurchasesPage;